import { type Dispatch, type SetStateAction, useState, useCallback, useMemo } from 'react';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { ClassificationOption, Color } from '@atlassian/data-classification-level';
import { useIntl } from '@atlassian/jira-intl';
import { getProjectDefaultClassificationOption } from '@atlassian/jira-issue-classification-badge/src/ui/edit-view-popup/utils.tsx';
import type {
	BaseField,
	FieldConfigValueWithoutBaseField,
	ClassificationFieldProps,
} from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { GIC_LIFECYCLE_EVENTS } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/constants.tsx';
import { useGICLifeCycleEvents } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/main.tsx';
import {
	fireTrackAnalyticsDeferred,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';

export const useClassificationState = (
	classificationOption: ClassificationOption | undefined,
): [
	ClassificationOption | undefined,
	Dispatch<SetStateAction<ClassificationOption | undefined>>,
	(val: ClassificationOption) => void,
] => {
	const [selectedClassification, setSelectedClassification] = useState<
		ClassificationOption | undefined
	>(classificationOption);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { publish } = useGICLifeCycleEvents();

	const onClassificationOptionChange = useCallback(
		(option: ClassificationOption) => {
			setSelectedClassification(option);
			const analyticsEvent = createAnalyticsEvent({});
			fireTrackAnalyticsDeferred(analyticsEvent, 'ClassificationInGIC changed');
			publish(GIC_LIFECYCLE_EVENTS.CLASSIFICATION_LEVEL_FIELD_CHANGE, {
				selectedClassification: option,
			});
		},
		[createAnalyticsEvent, publish],
	);

	return [selectedClassification, setSelectedClassification, onClassificationOptionChange];
};

export const useGetCreateClassificationLevel = () => {
	const { formatMessage } = useIntl();
	return useCallback(
		(value: ClassificationOption | undefined | null) => {
			if (!value) {
				const noClassificationOption: ClassificationOption = {
					id: undefined,
					definition: formatMessage(
						expVal('project-terminology-refresh', 'isEnabled', false)
							? messages.noClassificationOptionGalaxia
							: messages.noClassificationOption,
					),
					color: undefined,
					includeIcon: false,
				};

				return noClassificationOption;
			}

			return getProjectDefaultClassificationOption(
				formatMessage(
					expVal('project-terminology-refresh', 'isEnabled', false)
						? messages.projectDefaultClassificationOptionGalaxia
						: messages.projectDefaultClassificationOption,
				),
				value,
			);
		},
		[formatMessage],
	);
};

export const mapOptionsToClassificationOptions = (
	classificationFieldConfig: (BaseField & FieldConfigValueWithoutBaseField) | undefined,
) => {
	const allowedClassifications: ClassificationOption[] =
		classificationFieldConfig?.allowedValues?.map(
			(value: {
				node: { id: string; name: string; definition: string; color: { colorKey: string } };
			}) => mapOptionToClassificationOption(value.node),
		);

	return allowedClassifications;
};

export const mapOptionToClassificationOption = (
	defaultValue:
		| {
				id: string;
				name: string;
				color: { colorKey: string };
				definition: string;
		  }
		| null
		| undefined,
) => {
	const defaultClassification: ClassificationOption | undefined = defaultValue
		? {
				id: defaultValue?.id,
				name: defaultValue?.name,
				definition: defaultValue?.definition,
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				color: defaultValue?.color?.colorKey as Color,
			}
		: undefined;

	return defaultClassification;
};

export const useClassificationFieldProps = (
	classificationFieldConfig: (BaseField & FieldConfigValueWithoutBaseField) | undefined,
) => {
	const getCreateClassificationLevel = useGetCreateClassificationLevel();

	return useMemo(() => {
		let classificationFieldProps: ClassificationFieldProps = {
			availableClassifications: undefined,
			initialClassification: undefined,
			isDisabled: false,
			nonEditableMessage: undefined,
		};

		const allowedClassifications: ClassificationOption[] =
			mapOptionsToClassificationOptions(classificationFieldConfig);

		const initialDefaultClassification: ClassificationOption | undefined =
			mapOptionToClassificationOption(classificationFieldConfig?.defaultValue?.[0]);
		const projectDefaultClassification: ClassificationOption | undefined =
			mapOptionToClassificationOption(classificationFieldConfig?.defaultValue?.[1]);

		const projectLevelClassification = getCreateClassificationLevel(projectDefaultClassification);

		const isFieldEditable = classificationFieldConfig?.isEditable;

		if (allowedClassifications) {
			classificationFieldProps = {
				availableClassifications: allowedClassifications.concat(projectLevelClassification),
				initialClassification: initialDefaultClassification || projectLevelClassification,
				isDisabled: isFieldEditable === false,
				nonEditableMessage: classificationFieldConfig?.nonEditableReason,
			};
		}

		return classificationFieldProps;
	}, [classificationFieldConfig, getCreateClassificationLevel]);
};
